import {
  CoveragePlanCardUrl,
  CoveragePlanStatus,
  CoveragePlanType,
  PrescriptionDrugCardInfo
} from '@cms-max/medicare-api-sdk'
import { Badge, Card } from '@cmsgov/ds-medicare-gov'
import classNames from 'classnames'
import { MyInternalLink, MyLink, MyLinkClickHandler } from 'components/MyLink'
import { useEvent } from 'hooks/useEvent'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { DateUtil } from 'utils/date'

import classes from './PlanCard.module.scss'
import { usePlanCardLink } from './usePlanCardLink'

const DrugCoverageBadge = ({ type }: { type: CoveragePlanType }) => {
  const { t } = useTranslation('accountHome', {
    keyPrefix: 'currentPlans.drugCoverage'
  })

  if (![CoveragePlanType.Ma, CoveragePlanType.Mapd].includes(type)) return null

  const text = type === CoveragePlanType.Mapd ? t('yes') : t('no')
  const variation = type === CoveragePlanType.Mapd ? 'info' : 'warn'

  return (
    <div>
      <Badge variation={variation}>{text}</Badge>
    </div>
  )
}

type PlanCardProps = {
  type: CoveragePlanType
  title: string
  name: React.ReactNode
  rxCard?: PrescriptionDrugCardInfo
  startDate: string
  status: CoveragePlanStatus
  urls: Array<CoveragePlanCardUrl>
  onLinkClick: MyLinkClickHandler
}
export const PlanCard = ({
  name,
  rxCard,
  startDate,
  title,
  type,
  status,
  urls,
  onLinkClick
}: PlanCardProps) => {
  const { t, i18n } = useTranslation('accountHome')

  const flags = useFlags()

  const urlObj = usePlanCardLink({ language: i18n.language, type, urls })

  const { fireEvent } = useEvent('accountHome')

  const enrollmentDate = DateUtil.fromFormattedString(startDate, 'yyyy-MM-dd')
  const formattedDate = enrollmentDate.toServerDateString()

  const isOM = [CoveragePlanType.OmPartA, CoveragePlanType.OmPartB].includes(
    type
  )

  const showRxCardLink = flags?.useNewRxDrugCard && rxCard

  const createRxCardClickEventHandler = useCallback(
    function (): MyLinkClickHandler {
      return ({ linkText, linkUrl }) => {
        fireEvent('accounthome_temporarydrugcard_click', {
          linkText,
          linkUrl,
          planType: type
        })
      }
    },
    [fireEvent, type]
  )

  return (
    <Card
      className={`ds-u-padding--3 ds-u-display--flex ds-u-flex-direction--column ${classes.card}`}
    >
      <div className="ds-text-body--md">{title}</div>
      <div
        className={classNames('ds-text-body--lg ds-u-font-weight--semibold', {
          'my-u-whitespace-nowrap': isOM
        })}
      >
        {name}
      </div>
      <DrugCoverageBadge type={type} />
      <hr className="ds-u-border-bottom--1 my-u-width--full ds-u-margin-y--2" />
      <div className="my-u-flex-grow-1">
        <div className="ds-text-body--md ds-u-margin-bottom--0">
          {t('currentPlans.startDateHeading', { count: 1 })}
        </div>
        <div className="ds-text-body--lg ds-u-font-weight--semibold">
          {formattedDate}
        </div>
      </div>
      <div className="ds-text-body--md">
        {urlObj
          ? (
          <div>
            <MyLink href={urlObj.url} onClick={onLinkClick}>
              {urlObj.text}
            </MyLink>
          </div>
            )
          : null}
        {showRxCardLink
          ? (
          <div>
            <MyInternalLink
              to={{
                pathname: '/rx-drug-card',
                search: `?status=${status}`
              }}
              onClick={createRxCardClickEventHandler()}
            >
              {t('currentPlans.rxCardLink')}
            </MyInternalLink>
          </div>
            )
          : null}
      </div>
    </Card>
  )
}
